import * as angular from 'angular';
import { CellularDeviceCommunicationHardwareService } from '../../ng-app/cellular-device/cellular-device-communication-hardware.service';
import { ICellularCarrierService, ICellularDeviceAssignmentService, ICellularDeviceService } from '../../ng-app/cellular-device/services';
import { EditMetadata } from '../directives/EditableContent/models';
import { CellularDeviceInfo } from '../typings/api';
import { IUtilities } from '../typings/services';

// Duplicate for old angular, this component should be removed when working version of  
export class CellularDeviceSelectorController implements angular.IComponentController {
    public static $name: string = 'cellularDeviceSelectorController';
    public static $controllerAs: string = 'cellularDeviceSelectorCtrl';

    public static $inject = [
        '$q',
        '$scope',
        '$timeout',
        'cellularCarrierService',
        'cellularDeviceAssignmentService',
        'cellularDeviceCommunicationHardwareService',
        'cellularDeviceService',
        'utilities'
    ];

    /** The carrier currently selected by the dropdown */
    public selectedCarrier: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel;
    /** This is a list of ALL possible device assignments, it comes directly from the server */
    public assignments: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[];
    /** This is a list of ALL possible Communication Hardware, it comes directly from the server */
    public communicationHardware: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel[];
    /** This is a list of ALL possible Carriers, it comes directly from the server */
    public carriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[];
    /** This must be true for the 'Request Activation' button to appear at all */
    public canRequestActivation: boolean;
    /** This must be true for the 'Update Cellular Device Status' button to be present at all */
    public canRequestActivationStatus: boolean;
    /** This is a list of the carriers that are available for the currently selected device type */
    public currentCarriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[];
    /** This is a list of the device types that are available for the currently selected communication hardware */
    public currentDeviceTypes: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[];
    /** The assignment currently selected by the dropdown */
    public selectedAssignment: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel;
    /** The Communication Hardware currently selected by the dropdown */
    public selectedCommunicationHardware: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel;
    /** The device type currently selected by the dropdown */
    public selectedDeviceType: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel;
    /** The vendor currently selected by the dropdown */
    public selectedVendor: CASLink.Device.Ui.ViewModel.Modules.CellularVendorViewModel;
    /** A list of ALL possible devices, coming from the server */
    public devices: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[];
    /** The passed-in object of existing values & output object for changed values */
    public cellularDeviceInfo: CellularDeviceInfo;
    /** Validations */
    public formMetadata: EditMetadata;
    public canRequestDeactivation: boolean;
    /** This must be true to enable all Cellular Device controls */
    public canEditCellularDevice: boolean = false;

    public jobNumber: number;
    public locationId: number;
    public moduleId: number;

    public mustSaveBeforeActivation: boolean = false;

    /**
     * The name of the form -- since this will need to be included on the cl-inputs,
     * this allows us to change it once in code if we decide to rename
     * These two properties must be changed together if they are changed!!
     */
    public formName: string = `${CellularDeviceSelectorController.$controllerAs}.cellularDeviceForm`;
    public cellularDeviceForm: angular.IFormController;
    public onCellularDeviceStatusChanged: (params: {cellularDeviceStatusChange: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceStatusViewModel}) => void;

    /** Single source of knowledge for the placeholder when no carrier is selected */
    public noAssignment: string = 'None';
    public noCarrier: string = 'None';
    public noDevice: string = 'None';
    public noHardware: string = 'None';

    private decRegex = /^\d*$/;
    private hexRegex = /^[\d a-f A-F]*$/;

    private cellularDeviceStatus: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceStatusViewModel;

    constructor(
        private $q: angular.IQService,
        private $scope: any,
        private $timeout: angular.ITimeoutService,
        private cellularCarrierService: ICellularCarrierService,
        private cellularDeviceAssignmentService: ICellularDeviceAssignmentService,
        private cellularDeviceCommunicationHardwareService: CellularDeviceCommunicationHardwareService,
        private cellularDeviceService: ICellularDeviceService,
        private utilities: IUtilities,
    ) {
    }

    public $onInit() {
        this.initialize();
    }

    cellularDeviceStatusChanged(cellularDeviceStatusChange: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceStatusViewModel) {
        this.onCellularDeviceStatusChanged({ cellularDeviceStatusChange: cellularDeviceStatusChange });
    }

    private get showActivation() {
        if (this.mustSaveBeforeActivation) {
            return this.cellularDeviceForm && this.cellularDeviceForm.$pristine;
        }

        return this.cellularDeviceForm && this.cellularDeviceForm.$valid;
    }

    private get canUpdateCellularDeviceStatus() {
        return this.canRequestActivationStatus
            && this.selectedCarrier
            && this.selectedCarrier.vendors
            && this.selectedCarrier.vendors.length;
    }

    private filterCarriers(): CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[] {
        if (!this.selectedDeviceType) {
            return [];
        }

        return this.carriers.filter((carrier: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel) => {
            return this.selectedDeviceType.cellularCarriers
                .some((c: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel) => c.id === carrier.id);
        });
    }

    private filterDeviceTypes(): CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[] {
        if (!this.selectedCommunicationHardware) {
            return [];
        }

        return this.devices.filter(device => this.selectedCommunicationHardware.cellularDeviceTypes.some(d => d.id === device.id));
    }

    private getCellularCarriers(): angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[]> {
        return this.cellularCarrierService.getCellularCarriers()
            .then((carriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[]) => {
                return this.carriers = carriers;
            });
    }

    private getCellularDeviceAssignments(): angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[]> {
        return this.cellularDeviceAssignmentService.getCellularDeviceAssignments()
            .then((assignments: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[]) => {
                return this.assignments = assignments;
            });
    }

    private getCellularDeviceCommunicationHardware():
        angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceCommunicationHardwareViewModel[]> {
        return this.cellularDeviceCommunicationHardwareService.getCommunicationHardware().then(hardware => {
            return this.communicationHardware = hardware;
        });
    }

    private getCellularDevices(): angular.IPromise<CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[]> {
        return this.cellularDeviceService.getCellularDevices()
            .then((devices: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[]) => {
                return this.devices = devices;
            });
    }

    private initFormMetadata(): void {
        this.formMetadata = {
            fieldMetadata: {
                cellularCarrier: {
                    required: true
                },
                cellularDeviceAssignment: {
                    required: true
                },
                cellularVendor: {
                    required: true
                }
            }
        };
    }

    private initialize(): void {
        this.$q.all([
            this.getCellularDeviceCommunicationHardware(),
            this.getCellularDevices(),
            this.getCellularCarriers(),
            this.getCellularDeviceAssignments()
        ]).then(() => {
            this.initFormMetadata();
            this.populateCellularDeviceInfo();
            this.updateValidation();
            this.watchCellularDeviceInfoEntity();
        });
    }

    private populateCellularDeviceInfo() {
        /* Prepopulate the info if it is passed in to start */
        if (this.cellularDeviceInfo) {
            this.preselectCellularDeviceCommunicationHardware(this.cellularDeviceInfo.cellularDeviceCommunicationHardwareId);
            this.preselectCellularDeviceAssignment(this.cellularDeviceInfo.cellularDeviceAssignmentId);
            this.preselectCellularDeviceType(this.cellularDeviceInfo.cellularDeviceTypeId);
            this.preselectVendor(this.cellularDeviceInfo.cellularVendorId);
            this.currentCarriers = this.filterCarriers();
            this.currentDeviceTypes = this.filterDeviceTypes();
            this.preselectCarrier(this.cellularDeviceInfo.cellularCarrierId);
            this.populateIdentifiers(this.cellularDeviceInfo.cellularDeviceInfoIdentifiers);
        } else {
            /**
             * This whole else clause is for handling the case where the entity information
             * was NOT prepopulated, and we press 'Undo' after changing it
             * Without wrapping this in an $applyAsync,
             * you will see two distinct cycles happen where the carrier is cleared, then the deviceId is removed
             */

            this.$scope.$applyAsync(() => {
                this.selectedAssignment = null;
                this.selectedCarrier = null;

                /**
                 *  If not we want to populate the object with garbage data so our custom watcher
                 * will actually get hit when the Undo button is clicked :)
                 */
                this.cellularDeviceInfo = {
                    cellularCarrierId: null,
                    cellularCarrierName: this.noCarrier,
                    cellularDeviceAssignmentId: null,
                    cellularDeviceAssignmentName: this.noAssignment,
                    cellularDeviceCommunicationHardwareId: null,
                    cellularDeviceCommunicationHardwareName: this.noHardware,
                    cellularDeviceInfoIdentifiers: null,
                    cellularDeviceTypeId: null,
                    cellularDeviceTypeName: this.noDevice,
                    cellularVendorId: null,
                    moduleId: this.moduleId,
                    dataPlanExpirationVisible: false,
                    isFreeSubscription: false,
                    isFreeSubscriptionVisible: false,
                    allowedMobileIdTypes: null,
                    cellularVendorName: null,
                    dataPlanExpiration: null,
                    updatedCellularDeviceInfoIdentifiers: null
                } as CellularDeviceInfo;
                this.utilities.addGarbageData(this.cellularDeviceInfo);
            });
        }
    }

    private populateIdentifiers(identifiers: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceInfoIdentifierViewModel[]) {
        if (!this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers) {
            this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers = {};
        }

        if (this.selectedCarrier && identifiers) {
            identifiers.forEach((identifier: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceInfoIdentifierViewModel) => {
                this.cellularDeviceInfo.updatedCellularDeviceInfoIdentifiers[identifier.mobileIdTypeId] = identifier.deviceId;
            });
        }
    }

    private preselectCellularDeviceCommunicationHardware(cellularDeviceCommunicationHardwareId: string) {
        let filteredHardware = this.communicationHardware.filter(hardware => hardware.id === cellularDeviceCommunicationHardwareId);

        this.selectedCommunicationHardware = filteredHardware.length ? filteredHardware[0] : null;

        this.updateCellularDeviceCommunicationHardware();
    }

    private preselectCellularDeviceAssignment(cellularDeviceAssignmentId: string) {
        let filteredAssignments: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel[] =
            this.assignments.filter((assignment: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceAssignmentViewModel) => {
                return cellularDeviceAssignmentId === assignment.id;
            });

        this.selectedAssignment = filteredAssignments.length ? filteredAssignments[0] : null;

        this.updateCellularDeviceAssignment();
    }

    private preselectCellularDeviceType(cellularDeviceTypeId: string) {
        let filteredDeviceTypes: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel[];

        /** If there's only one device type, nothing else matters. Select it. */
        if (this.currentDeviceTypes.length === 1) {
            filteredDeviceTypes = [this.currentDeviceTypes[0]];
        } else {
            filteredDeviceTypes =
                this.currentDeviceTypes.filter((device: CASLink.Device.Ui.ViewModel.Modules.CellularDeviceTypeViewModel) => {
                    return cellularDeviceTypeId === device.id;
                });
        }

        /**
         * The potential null assignment handles this case:
         * 1. The component was entered with no cellularDeviceInfo & this function populates it with the noDevice name & garbage data
         * 2. The component was destroyed (ng-if), but that cellularDeviceInfo still exists on the parent scope
         * 3. The component is recreated -- we'll now be in this block with no matching devices
         */
        this.selectedDeviceType = filteredDeviceTypes.length ? filteredDeviceTypes[0] : null;

        this.updateCellularDeviceType();
    }

    private preselectCarrier(cellularCarrierId: string) {
        let filteredCarriers: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel[];

        /* If there's only one carrier, nothing else matters. Select it. */
        if (this.currentCarriers.length === 1) {
            filteredCarriers = [this.currentCarriers[0]];
        } else {
            filteredCarriers = this.currentCarriers.filter((carrier: CASLink.Device.Ui.ViewModel.Modules.CellularCarrierViewModel) => {
                return cellularCarrierId === carrier.id;
            });
        }

        /**
         * The potential null assignment handles this case:
         * 1. The component was entered with no cellularDeviceInfo & this function populates it with the noCarrier name & garbage data
         * 2. The component was destroyed (ng-if), but that cellularDeviceInfo still exists on the parent scope
         * 3. The component is recreated -- we'll now be in this block with no matching carriers
         */
        this.selectedCarrier = filteredCarriers.length ? filteredCarriers[0] : null;

        this.updateCellularCarrier();
    }

    private preselectVendor(cellularVendorId: string) {
        let filteredVendors: CASLink.Device.Ui.ViewModel.Modules.CellularVendorViewModel[];

        if (!this.selectedCarrier) {
            return null;
        }

        /* If there's only one vendor, nothing else matters. Select it. */
        // tslint:disable-next-line: prefer-conditional-expression
        if (this.selectedCarrier.vendors.length === 1) {
            filteredVendors = [this.selectedCarrier.vendors[0]];
        } else {
            filteredVendors = this.selectedCarrier.vendors.filter(vendor => vendor.id === cellularVendorId);
        }

        this.selectedVendor = filteredVendors.length ? filteredVendors[0] : null;

        this.updateVendor();
    }

    private touchInputs() {
        /**
         * If we had prepopulated Cellular Device Info info, and switch carriers,
         * then we should show the old number as invalid if it still is --
         * but not if we just start typing from a fresh one (so no md-is-error on the md-input-container)
         */
        let deviceIdInput = this.cellularDeviceForm ? this.cellularDeviceForm['cellularDeviceId'] : null;

        if (deviceIdInput) {
            deviceIdInput.$setTouched();
        }
    }

    private updateCellularDeviceAssignment() {
        this.cellularDeviceInfo.cellularDeviceAssignmentId = this.selectedAssignment ? this.selectedAssignment.id : null;
        this.cellularDeviceInfo.cellularDeviceAssignmentName = this.selectedAssignment ? this.selectedAssignment.name : this.noAssignment;
    }

    private updateCellularDeviceCommunicationHardware() {
        this.cellularDeviceInfo.cellularDeviceCommunicationHardwareId =
            this.selectedCommunicationHardware ? this.selectedCommunicationHardware.id : null;

        this.cellularDeviceInfo.cellularDeviceCommunicationHardwareName =
            this.selectedCommunicationHardware ? this.selectedCommunicationHardware.name : this.noHardware;

        this.currentDeviceTypes = this.filterDeviceTypes();
        this.preselectCellularDeviceType(this.cellularDeviceInfo.cellularDeviceTypeId);

        this.updateCellularDeviceType();
    }

    private updateCellularDeviceType() {
        this.cellularDeviceInfo.cellularDeviceTypeId = this.selectedDeviceType ? this.selectedDeviceType.id : null;
        this.cellularDeviceInfo.cellularDeviceTypeName = this.selectedDeviceType ? this.selectedDeviceType.name : this.noDevice;

        this.currentCarriers = this.filterCarriers();
        this.preselectCarrier(this.cellularDeviceInfo.cellularCarrierId);

        this.updateCellularCarrier();
    }

    private  updateCellularCarrier() {
        this.cellularDeviceInfo.cellularCarrierId = this.selectedCarrier ? this.selectedCarrier.id : null;
        this.cellularDeviceInfo.cellularCarrierName = this.selectedCarrier ? this.selectedCarrier.name : this.noCarrier;
        this.cellularDeviceInfo.allowedMobileIdTypes = this.selectedCarrier ? this.selectedCarrier.mobileIdTypes : [];

        this.updateValidation();
        this.preselectVendor(this.cellularDeviceInfo.cellularVendorId);

        this.$timeout(() => {
            this.touchInputs();
        });
    }

    private updateVendor() {
        this.cellularDeviceInfo.cellularVendorId = this.selectedVendor ? this.selectedVendor.id : null;
        this.cellularDeviceInfo.cellularVendorName = this.selectedVendor ? this.selectedVendor.displayName : null;
    }

    private updateValidation() {
        if (this.selectedCarrier) {
            for (let mobileIdTypeKey in this.selectedCarrier.mobileIdTypes) {
                let currentMobileIdType = this.selectedCarrier.mobileIdTypes[mobileIdTypeKey];
                const hexBase = 16;
                let isHex = currentMobileIdType.base === hexBase;
                let regex = isHex ? this.hexRegex : this.decRegex;

                this.formMetadata.fieldMetadata[currentMobileIdType.name] = {
                    maxLength: currentMobileIdType.maxChars,
                    minLength: currentMobileIdType.minChars,
                    pattern: regex,
                    patternMsg: `Characters allowed: ${isHex ? '(0-9) (A-F)' : '(0-9)'}`,
                    required: true
                };
            }
        }
    }

    /**
     * If we change the cellularDeviceInfo from outside (Undo) then we should repopulate the dropdown.
     * This should not be called when editing the viewmodel from within the component's scope
     */
    private watchCellularDeviceInfoEntity() {
        this.$scope.$watch(() => this.cellularDeviceInfo, (newVal, oldVal) => {
            if (!angular.equals(newVal, oldVal)) {
                this.populateCellularDeviceInfo();
            }
        });
    }
}
